import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { useContext } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import { SanityIcon } from 'components/SanityIcon';
import { ShortArrow } from 'components/icons';
import { Button } from 'components/rebrand/Button';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { LocaleContext } from 'components/rebrand/Layout';

import { IInstruction } from 'typescriptDefs/pages/new-to-canada';

import { useIsMobileSize } from 'helpers';

type Props = {
  ctaDescription: string;
  ctaText: string;
  ctaUrl: string;
  heading: PortableTextBlock | PortableTextBlock[];
  instructions: IInstruction[];
};

const SlideContent = ({ instruction }: { instruction: IInstruction }) => {
  return (
    <div className="h-full w-full rounded-12 bg-primary-75 p-32 md:w-360">
      <SanityIcon className="mb-20" icon={{ img: instruction.img, altText: instruction.imgAltText }} />
      <div className="mb-8 text-3xl font-bold leading-133 text-primary-350">{instruction.label}</div>
      <p className="text-md leading-normal text-grey-500">{instruction.text}</p>
    </div>
  );
};

const Instructions = ({ ctaDescription, ctaText, ctaUrl, heading, instructions }: Props) => {
  const isMobile = useIsMobileSize();
  const locale = useContext(LocaleContext);

  return (
    <Section className="bg-primary-500 pb-64 pt-40 text-white md:py-64">
      <Container className="overflow-visible px-32 xs:px-0">
        <div className="mx-auto mb-32 flex flex-col items-center text-center md:items-start md:text-left [&_h2]:mb-8 [&_h2]:text-secondary-75 md:[&_h2]:mb-16 md:[&_h2]:w-3/5 md:[&_p]:w-1/2">
          <CustomPortableText content={heading} />
        </div>

        <div className="mb-64">
          <div className="mb-64 ml-auto hidden w-fit flex-row gap-8 md:flex">
            <Button variant="tertiaryInverse" className="new-to-canada-prev p-8">
              <ShortArrow className="h-24 w-24 rotate-180" />
            </Button>
            <Button variant="tertiaryInverse" className="new-to-canada-next p-8">
              <ShortArrow className="h-24 w-24" />
            </Button>
          </div>
          {isMobile ? (
            <div className="grid gap-32">
              {instructions.map((instruction) => (
                <SlideContent key={instruction.label} instruction={instruction} />
              ))}
            </div>
          ) : (
            <Swiper
              slidesPerView="auto"
              spaceBetween={32}
              modules={[A11y, Keyboard, Navigation, Pagination]}
              a11y={{
                prevSlideMessage: {
                  en: 'Previous slide',
                  fr: 'Diapositive précédente',
                }[locale],
                nextSlideMessage: { en: 'Next slide', fr: 'Diapositive suivante' }[locale],
              }}
              keyboard
              navigation={{ enabled: true, nextEl: '.new-to-canada-next', prevEl: '.new-to-canada-prev' }}
              pagination={{
                clickable: true,
                el: `.new-to-canada-pagination`,
                type: 'bullets',
              }}
              className="!overflow-visible"
            >
              {instructions.map((instruction, index) => (
                <SwiperSlide key={index} className="!h-auto !w-fit">
                  <SlideContent instruction={instruction} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          <div
            className={twMerge(
              clsx(
                'new-to-canada-pagination mt-32 !hidden !w-fit flex-row items-center rounded-full bg-white bg-opacity-10 p-8 md:!flex',
                '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:!bg-primary-100 [&_.swiper-pagination-bullet]:mx-6 [&_.swiper-pagination-bullet]:!bg-white [&_.swiper-pagination-bullet]:!opacity-100',
                '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:h-12 [&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:w-12',
              ),
            )}
          />
        </div>
        <div className="flex flex-col">
          <p className="mb-32 text-center text-xl leading-normal md:text-left">{ctaDescription}</p>
          <a className="mx-auto block w-fit md:mx-0" href={ctaUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="primaryInverse">{ctaText}</Button>
          </a>
        </div>
      </Container>
    </Section>
  );
};

export default Instructions;
